import { useState, useEffect } from 'react';
import { X, Shield, Eye, CheckCircle } from 'lucide-react';
import { enableTracking, enableSessionRecording, disableTracking, captureEvent } from '../utils/posthog';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function ConsentBanner() {
  const [show, setShow] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [sessionRecordingConsent, setSessionRecordingConsent] = useState(false);

  useEffect(() => {
    // Nettoyer les anciens cookies Google Analytics (migration)
    const oldGoogleConsent = localStorage.getItem('cookie_consent');
    const oldGoogleConsentDate = localStorage.getItem('cookie_consent_date');
    if (oldGoogleConsent || oldGoogleConsentDate) {
      console.log('🧹 Nettoyage des anciennes préférences Google Analytics...');
      localStorage.removeItem('cookie_consent');
      localStorage.removeItem('cookie_consent_date');
      localStorage.removeItem('analytics_consent'); // Ancien format simple
    }

    // Vérifier si le consentement PostHog a déjà été donné
    const savedConsent = localStorage.getItem('analytics_consent');

    if (!savedConsent) {
      // Afficher le bandeau après 1 seconde
      const timer = setTimeout(() => setShow(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // Appliquer le consentement sauvegardé
      try {
        const consent = JSON.parse(savedConsent);
        applyConsent(consent);
      } catch (error) {
        console.error('Erreur parsing consent:', error);
        setShow(true);
      }
    }
  }, []);

  const applyConsent = (consent) => {
    if (consent.tracking) {
      enableTracking();
    } else {
      disableTracking();
    }

    if (consent.sessionRecording) {
      enableSessionRecording();
    }
  };

  const saveConsent = async (consent) => {
    const consentData = {
      tracking: consent.tracking,
      sessionRecording: consent.sessionRecording,
      date: new Date().toISOString()
    };

    // Sauvegarder localement
    localStorage.setItem('analytics_consent', JSON.stringify(consentData));

    // Appliquer immédiatement
    applyConsent(consentData);

    // Event PostHog (avant activation si refusé)
    captureEvent('consent_updated', {
      consent_tracking: consent.tracking,
      consent_session_recording: consent.sessionRecording
    });

    // Envoyer au backend (si utilisateur connecté)
    try {
      await api.post('/users/me/consent', {
        analyticsConsent: consent.tracking,
        sessionRecordingConsent: consent.sessionRecording
      });
    } catch (error) {
      console.log('Utilisateur non connecté ou erreur sauvegarde consent:', error.message);
    }
  };

  const handleAcceptAll = async () => {
    const consent = {
      tracking: true,
      sessionRecording: true
    };

    await saveConsent(consent);
    toast.success('Préférences enregistrées');
    setShow(false);
  };

  const handleRejectAll = async () => {
    const consent = {
      tracking: false,
      sessionRecording: false
    };

    await saveConsent(consent);
    toast.success('Préférences enregistrées');
    setShow(false);
  };

  const handleCustomSave = async () => {
    const consent = {
      tracking: analyticsConsent,
      sessionRecording: sessionRecordingConsent
    };

    await saveConsent(consent);
    toast.success('Préférences enregistrées');
    setShow(false);
    setShowCustomize(false);
  };

  if (!show) return null;

  return (
    <>
      {/* Bandeau principal */}
      {!showCustomize && (
        <div className="fixed bottom-0 left-0 right-0 bg-white text-gray-900 p-6 shadow-2xl z-50 border-t-2 border-gray-200">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Shield className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Respect de votre vie privée</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  Nous utilisons des cookies et des technologies similaires pour améliorer votre expérience,
                  analyser l'utilisation de notre plateforme et mesurer l'efficacité de nos fonctionnalités.
                  Vos données sont traitées conformément au <a href="/privacy" className="text-blue-600 underline hover:text-blue-700">RGPD</a>.
                </p>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Tout accepter
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors"
                  >
                    Tout refuser
                  </button>
                  <button
                    onClick={() => setShowCustomize(true)}
                    className="px-6 py-2.5 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium transition-colors"
                  >
                    Personnaliser
                  </button>
                </div>
              </div>
              <button
                onClick={handleRejectAll}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de personnalisation */}
      {showCustomize && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Préférences de confidentialité</h2>
              <button
                onClick={() => setShowCustomize(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Option 1 : Analytics */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-blue-600" />
                      <h3 className="font-semibold text-gray-900">Cookies analytiques</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      Nous permettent de mesurer l'audience, comprendre comment vous utilisez Orbloom
                      et améliorer nos fonctionnalités. Les données sont anonymisées.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Outil : PostHog (hébergé en Europe, conforme RGPD)
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={analyticsConsent}
                      onChange={(e) => setAnalyticsConsent(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>

              {/* Option 2 : Session Recording */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Eye className="w-5 h-5 text-purple-600" />
                      <h3 className="font-semibold text-gray-900">Enregistrement de session</h3>
                    </div>
                    <p className="text-sm text-gray-600">
                      Enregistre votre parcours sur Orbloom (clics, scroll) pour détecter les bugs
                      et améliorer l'expérience utilisateur. Les données sensibles sont masquées.
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      Note : Les mots de passe et champs sensibles ne sont jamais enregistrés
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sessionRecordingConsent}
                      onChange={(e) => setSessionRecordingConsent(e.target.checked)}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                  </label>
                </div>
              </div>

              {/* Note RGPD */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Vos droits :</strong> Vous pouvez à tout moment modifier ces préférences,
                  accéder à vos données, demander leur suppression ou vous opposer à leur traitement.
                  Consultez notre <a href="/privacy" className="underline font-medium">politique de confidentialité</a>.
                </p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex gap-3">
              <button
                onClick={() => setShowCustomize(false)}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
              <button
                onClick={handleCustomSave}
                className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Enregistrer mes préférences
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
